// CSV and PDF export for chat bot results.
//
// Both take the rows the endpoint returned and the RESULT_COLUMNS definition, so
// the file the user downloads holds the same columns in the same order as the
// table they were looking at. The filter recap rides along in both: a
// bot-generated export is only readable later if it says what was asked for.

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { RESULT_COLUMNS, exportValue } from "./chatbotConversation.js";

// RPD brand palette, matching the Market Overview export.
const C = {
  navy: [18, 40, 63],
  coral: [252, 88, 104],
  fg2: [84, 84, 84],
  fg3: [138, 138, 138],
  line: [232, 234, 237],
  headBg: [18, 40, 63],
};

const stamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escCsv = (v) => {
  const s = String(v);
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};

/**
 * Downloads the rows as CSV.
 *
 * The filter recap goes in as comment lines above the header rather than into a
 * second sheet: a spreadsheet opens the file straight into the data, and the
 * recap is what tells the reader six weeks later which question produced it.
 *
 * @param {Array} rows      Result rows from api/chatbot/query.
 * @param {Array} summary   [{ text }] from summariseAnswers().
 * @param {string} sql      The generated SQL, recorded as a trailing comment.
 */
export const exportChatbotCsv = (rows, summary = [], sql = "") => {
  const columns = RESULT_COLUMNS;
  const lines = [];

  lines.push(`# RPD Chat Bot results — ${stamp()}`);
  (summary || []).forEach((s) => lines.push(`# ${s.text}`));
  lines.push(`# Rows: ${(rows || []).length}`);
  lines.push("");

  lines.push(columns.map((c) => escCsv(c.title)).join(","));
  (rows || []).forEach((row) => {
    lines.push(
      columns
        .map(({ field }) => escCsv(exportValue(field, row[field])))
        .join(",")
    );
  });

  if (sql) {
    lines.push("");
    // Newlines inside a CSV comment would look like data rows, so the statement
    // is flattened to one line.
    lines.push(`# SQL: ${sql.replace(/\s+/g, " ").trim()}`);
  }

  // BOM so Excel reads the file as UTF-8 rather than as the system codepage.
  triggerDownload(
    new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" }),
    `rpd-chatbot-${stamp()}.csv`
  );
};

/**
 * Downloads the rows as a landscape PDF.
 *
 * @param {Array} rows      Result rows from api/chatbot/query.
 * @param {Array} summary   [{ text }] from summariseAnswers().
 * @param {number} total    Total matches, which can exceed rows.length.
 */
export const exportChatbotPdf = (rows, summary = [], total = null) => {
  const columns = RESULT_COLUMNS;
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const PW = doc.internal.pageSize.width;
  const M = 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...C.navy);
  doc.text("Chat Bot Results", M, 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...C.fg3);
  doc.text(`Real Property Data · ${stamp()}`, PW - M, 14, { align: "right" });

  doc.setDrawColor(...C.coral);
  doc.setLineWidth(0.6);
  doc.line(M, 17, PW - M, 17);

  // Filter recap. Wrapped rather than truncated — with every filter answered the
  // line runs well past the page width, and a clipped recap is worse than a
  // taller header block.
  let y = 22;
  doc.setFontSize(8.5);
  doc.setTextColor(...C.fg2);
  const recap = (summary || []).map((s) => s.text);
  const recapText = recap.length ? recap.join("   ·   ") : "No filters applied";
  doc.splitTextToSize(recapText, PW - M * 2).forEach((line) => {
    doc.text(line, M, y);
    y += 4;
  });

  const shown = (rows || []).length;
  doc.setTextColor(...C.fg3);
  doc.setFontSize(8);
  doc.text(
    total !== null && total > shown
      ? `${shown} of ${total} matching rows`
      : `${shown} matching row${shown === 1 ? "" : "s"}`,
    M,
    y + 1
  );

  autoTable(doc, {
    startY: y + 5,
    margin: { left: M, right: M },
    head: [columns.map((c) => c.title)],
    body: (rows || []).map((row) =>
      columns.map(({ field }) => exportValue(field, row[field]))
    ),
    styles: {
      font: "helvetica",
      fontSize: 7,
      cellPadding: 1.6,
      lineColor: C.line,
      lineWidth: 0.1,
      textColor: C.navy,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: C.headBg,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7,
    },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    columnStyles: columns.reduce((acc, c, i) => {
      if (c.numeric) acc[i] = { halign: "right" };
      return acc;
    }, {}),
    didDrawPage: () => {
      const PH = doc.internal.pageSize.height;
      doc.setFontSize(7);
      doc.setTextColor(...C.fg3);
      doc.text("realpropertydata.ca", M, PH - 6);
      doc.text(
        `Page ${doc.internal.getNumberOfPages()}`,
        PW - M,
        PH - 6,
        { align: "right" }
      );
    },
  });

  doc.save(`rpd-chatbot-${stamp()}.pdf`);
};
